import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import {
  confirmPaymentMethods,
  extractPaymentMethods,
  listPaymentMethods,
  purchaseCreditPack,
  syncPaymentMethods,
} from '../../services/billingService';
import { fetchOrganizationSubscription } from '../../features/subscription/subscriptionSlice';
import { formatPayPerEnvelopePrice } from '../../utils/subscription';
import {
  formatAmountCents,
  getBillingErrorMessage,
  normalizePaymentFlowData,
} from '../../utils/billingPaymentFlow';
import { useStripeConfig } from './VolksignStripeProvider';
import PaymentMethodSelector from './PaymentMethodSelector';
import BillingPaymentModal from './BillingPaymentModal';

const CreditPackPurchaseModal = ({
  show,
  onHide,
  pack,
  onPurchaseComplete,
}) => {
  const dispatch = useDispatch();
  const { ready: stripeReady, error: stripeConfigError } = useStripeConfig();

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  const [paymentSelection, setPaymentSelection] = useState('saved');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [paymentModal, setPaymentModal] = useState({
    show: false,
    clientSecret: null,
    amountCents: null,
    currency: 'usd',
    paymentIntentId: null,
  });

  const amountCents = pack?.amountCents
    ?? (pack?.price != null ? Math.round(Number(pack.price) * 100) : null);
  const currency = pack?.currency || 'usd';
  const priceLabel =
    formatAmountCents(amountCents, currency)
    || formatPayPerEnvelopePrice(amountCents ?? 0, currency);
  const packLabel = pack?.label || pack?.code || 'Credit pack';

  const loadPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try {
      let data = await listPaymentMethods();
      let { methods, defaultId } = extractPaymentMethods(data);
      if (defaultId && methods.length === 0) {
        data = await syncPaymentMethods();
        ({ methods, defaultId } = extractPaymentMethods(data));
      }
      setPaymentMethods(methods);
      setDefaultPaymentMethodId(defaultId);
      if (methods.length > 0) {
        setPaymentSelection('saved');
        const initial =
          methods.find((pm) => pm.id === defaultId || pm.is_default) ?? methods[0];
        setSelectedPaymentMethodId(initial?.id ?? null);
      } else {
        setPaymentSelection('new');
      }
    } catch {
      setPaymentMethods([]);
      setPaymentSelection('new');
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  useEffect(() => {
    if (!show) {
      setError('');
      setPaymentModal({
        show: false,
        clientSecret: null,
        amountCents: null,
        currency: 'usd',
        paymentIntentId: null,
      });
      return undefined;
    }
    loadPaymentMethods();
    return undefined;
  }, [show, loadPaymentMethods]);

  const finishPurchase = useCallback(async (flowEnvelopeCredits = null) => {
    try {
      await dispatch(fetchOrganizationSubscription()).unwrap();
    } catch {
      // UI can still show success; next /user refresh will catch up.
    }
    onPurchaseComplete?.(flowEnvelopeCredits);
    onHide?.();
  }, [dispatch, onHide, onPurchaseComplete]);

  const startPurchase = useCallback(async () => {
    if (!pack?.code) {
      setError('Pack code is missing.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const useSaved = paymentSelection === 'saved' && paymentMethods.length > 0;

      const response = await purchaseCreditPack({
        packCode: pack.code,
        paymentMethodSelection: useSaved ? 'saved' : 'new',
        paymentMethodId: useSaved ? selectedPaymentMethodId : undefined,
        useDefaultPaymentMethod:
          useSaved && !selectedPaymentMethodId && Boolean(defaultPaymentMethodId),
        savePaymentMethod: useSaved ? undefined : savePaymentMethod,
        setAsDefault: useSaved ? undefined : savePaymentMethod,
      });

      if (!response.success) {
        throw new Error(response.message || 'Could not start credit pack purchase.');
      }

      const flow = normalizePaymentFlowData(response.data);

      if (flow.paymentCompleted) {
        await finishPurchase(flow.envelopeCredits);
        return;
      }

      if (flow.requiresPaymentUi && flow.clientSecret) {
        setPaymentModal({
          show: true,
          clientSecret: flow.clientSecret,
          amountCents: flow.amountCents ?? amountCents,
          currency: flow.currency ?? currency,
          paymentIntentId: flow.paymentIntentId,
        });
        return;
      }

      if (flow.requiresPaymentUi && !flow.clientSecret) {
        throw new Error('Payment could not be started. Try a saved card or retry.');
      }

      throw new Error('Unexpected payment response.');
    } catch (err) {
      setError(getBillingErrorMessage(err, 'Could not start credit pack purchase.'));
    } finally {
      setSubmitting(false);
    }
  }, [
    pack,
    paymentSelection,
    paymentMethods.length,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    savePaymentMethod,
    amountCents,
    currency,
    finishPurchase,
  ]);

  const handlePaymentModalSuccess = async ({ paymentIntentId } = {}) => {
    const piId = paymentIntentId || paymentModal.paymentIntentId;
    try {
      if (piId) {
        await confirmPaymentMethods({
          paymentIntentId: piId,
          setAsDefault: savePaymentMethod,
        });
      }
    } catch {
      // Confirm may still apply credits; refresh subscription next.
    }
    setPaymentModal((prev) => ({ ...prev, show: false }));
    await finishPurchase();
  };

  const payDisabled =
    submitting
    || !stripeReady
    || !pack?.code
    || (paymentSelection === 'saved' && !selectedPaymentMethodId && !defaultPaymentMethodId);

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Purchase {packLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-2">
            Prepaid envelope credits never expire. They are used after your plan&apos;s included
            monthly envelopes are exhausted.
          </p>
          <p className="mb-3">
            {pack?.envelopes != null && (
              <>
                <strong>{pack.envelopes}</strong> envelopes for{' '}
              </>
            )}
            <strong>{priceLabel}</strong>
          </p>

          {stripeConfigError && (
            <Alert variant="warning" className="small">{stripeConfigError}</Alert>
          )}

          {error && (
            <Alert variant="danger" className="small">
              {error}
            </Alert>
          )}

          {loadingPaymentMethods ? (
            <div className="text-center py-3">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <PaymentMethodSelector
              methods={paymentMethods}
              defaultId={defaultPaymentMethodId}
              selection={paymentSelection}
              onSelectionChange={setPaymentSelection}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onPaymentMethodIdChange={setSelectedPaymentMethodId}
              savePaymentMethod={savePaymentMethod}
              onSavePaymentMethodChange={setSavePaymentMethod}
              disabled={submitting}
              idPrefix="credit-pack-pay"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={startPurchase} disabled={payDisabled}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing…
              </>
            ) : (
              `Pay ${priceLabel}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <BillingPaymentModal
        show={paymentModal.show}
        onHide={() => setPaymentModal((prev) => ({ ...prev, show: false }))}
        title={`Pay ${priceLabel} for ${packLabel}`}
        amountCents={paymentModal.amountCents}
        currency={paymentModal.currency}
        clientSecret={paymentModal.clientSecret}
        returnUrl={`${window.location.origin}${process.env.PUBLIC_URL || ''}/plans?tab=credit_packs`}
        submitLabel={`Pay ${priceLabel}`}
        onSuccess={handlePaymentModalSuccess}
        onError={(err) => setError(getBillingErrorMessage(err))}
      />
    </>
  );
};

export default CreditPackPurchaseModal;
